import React, { Component } from 'react';
import { Alert } from '@material-ui/lab';
import { Button, IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import PropTypes from 'prop-types';

// Redux
import { connect } from 'react-redux';
import { removeAlert } from '../redux/actions/alertActions';
import AnnouncementBar from '../components/AnnouncementBar';
import { removeAnnouncement } from '../redux/actions/announcementActions';
import { redialEmergencyCall } from '../api';

/**
 * @extends {Component<{announcements: import('../redux/actions/announcementActions').Announcement[]; alerts: { id: string; msg: string; severity: 'error' | 'warning' | 'success' | 'info'; showClose: boolean }[], removeAlert: (id: string) => unknown, removeAnnouncement: (id: string) => unknown}>}
 */
class AlertList extends Component {
  constructor(props) {
    super(props);

    // Tracked per announcement id so one redial in flight cannot disable the
    // button on an unrelated bar.
    this.state = { redialingIds: [] };
  }

  handleRedial = (announcementId) => {
    this.setState(state => ({ redialingIds: [...state.redialingIds, announcementId] }));

    redialEmergencyCall().then(() => {
      // The redial is its own attempt with its own record, so this bar is now
      // stale. If the new call also fails, polling raises a fresh one.
      this.props.removeAnnouncement(announcementId);
    }).catch(() => {
      // Leave the bar up: the redial did not get through either, and the
      // instruction to dial 911 manually still stands.
    }).finally(() => {
      this.setState(state => ({
        redialingIds: state.redialingIds.filter(id => id !== announcementId)
      }));
    });
  };

  /**
   * MUI's Alert drops its own close button as soon as `action` is provided, so
   * anything actionable has to supply the close affordance itself.
   */
  renderAnnouncementAction(announcement) {
    const showClose = announcement.showClose ?? true;

    if (announcement.actionType !== 'REDIAL_EMERGENCY_CALL') return undefined;

    const isRedialing = this.state.redialingIds.includes(announcement.id);

    return (
      <>
        <Button
          size="small"
          variant="outlined"
          disabled={isRedialing}
          onClick={() => this.handleRedial(announcement.id)}
          style={{ color: announcement.color?.text, borderColor: announcement.color?.text }}
        >
          {isRedialing ? 'Redialing…' : (announcement.actionLabel ?? 'Redial')}
        </Button>
        {showClose && (
          <IconButton
            size="small"
            aria-label="close"
            onClick={() => this.props.removeAnnouncement(announcement.id)}
            style={{ color: announcement.color?.text, marginLeft: 8 }}
          >
            <Close fontSize="small" />
          </IconButton>
        )}
      </>
    );
  }

  render() {
    // Notices (errors from the server, confirmations) are bars under the
    // header, where they are seen as they happen. The header's bell is only
    // for what needs attention on campus. Every notice can be dismissed.
    const { alerts, announcements } = this.props;

    return (
      <>
        {alerts.map((alert) => (
          <Alert key={alert.id} severity={alert.severity} onClose={() => this.props.removeAlert(alert.id)}>
            {alert.msg}
          </Alert>
        ))}
        {announcements.map((announcement) => (
          <AnnouncementBar key={announcement.id} title={announcement.title} message={announcement.message} severity={announcement.severity} color={announcement.color} action={this.renderAnnouncementAction(announcement)} onClose={announcement.showClose ?? true ? () => this.props.removeAnnouncement(announcement.id) : undefined} />
        ))}
      </>
    );
  }
}

const mapStateToProps = state => ({
  alerts: state.alerts,
  announcements: state.announcements
});

const mapDispatchToProps = dispatch => ({
  removeAlert: id => dispatch(removeAlert(id)),
  removeAnnouncement: id => dispatch(removeAnnouncement(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(AlertList);

AlertList.propTypes = {
  alerts: PropTypes.array.isRequired,
  announcements: PropTypes.array.isRequired,
  removeAlert: PropTypes.func.isRequired,
  removeAnnouncement: PropTypes.func.isRequired
};
